// Draws channelization schemes: one strip per visible scheme under the tape,
// and faint edges continued down the heat canvas. Layout is separated from
// drawing so the export can reuse it and tests can check it without a canvas.

import { colToHz, colToX, hzToCol, rowToY, timeToRow, xToCol, type Clip, type View } from '../render/layout.ts'
import { formatHz, formatMHz } from '../units.ts'
import type { ResolvedFilter } from './filter.ts'
import type { Scheme } from './scheme.ts'

export const STRIP_HEIGHT = 16
const STRIP_FONT = '10px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'
/** Below this spacing, channel edges on the heat would merge into a wash. */
const MIN_EDGE_GAP_PX = 6
const LABEL_PAD_PX = 6

export interface VisibleScheme {
  scheme: Scheme
  color: string
}

export interface StripSpan {
  kind: 'band' | 'channel'
  /** Clamped to the strip; the unclamped edges are in `edge0`/`edge1`. */
  x0: number
  x1: number
  edge0: number
  edge1: number
  label: string | null
}

export interface StripMarker {
  x: number
  label: string | null
}

export interface StripLayout {
  scheme: Scheme
  color: string
  spans: StripSpan[]
  markers: StripMarker[]
  /** Channel edges to continue down the heat, or empty when too dense. */
  channelEdges: number[]
  bandEdges: number[]
}

export type Measure = (text: string) => number

/** Approximate text width when no canvas is available (tests). */
export const estimateMeasure: Measure = (text) => text.length * 5.6

export function layoutStrips(
  visible: readonly VisibleScheme[],
  clip: Clip,
  view: View,
  widthPx: number,
  measure: Measure,
): StripLayout[] {
  const minHz = colToHz(clip, view.col0)
  const maxHz = colToHz(clip, view.col1)
  const xOf = (hz: number) => colToX(view, hzToCol(clip, hz), widthPx)
  const clampX = (x: number) => Math.min(widthPx, Math.max(0, x))

  return visible.map(({ scheme, color }) => {
    const spans: StripSpan[] = []
    const channelEdges: number[] = []
    const bandEdges: number[] = []

    // Labels are placed in priority order into one collision list, so a later
    // label never overprints an earlier one: band names carry the most
    // meaning, a marker (a calling frequency, a preset's default slot) is next,
    // and slot numbers, which the ruler already implies, go last. Overlapping
    // channels in one scheme, as LoRaWAN's 500 kHz and 125 kHz uplinks are,
    // resolve the same way, left to right.
    const placed: { x0: number; x1: number }[] = []
    const place = (text: string, x0: number, x1: number, align: 'center' | 'left'): string | null => {
      const w = measure(text)
      const l = align === 'center' ? (x0 + x1) / 2 - w / 2 : x0
      const r = l + w
      if (align === 'center' && w + LABEL_PAD_PX > x1 - x0) return null
      if (l < 0 || r > widthPx) return null
      if (placed.some((p) => l < p.x1 + 2 && r > p.x0 - 2)) return null
      placed.push({ x0: l, x1: r })
      return text
    }

    const bandSpans: StripSpan[] = []
    for (const band of scheme.bands) {
      if (band.highHz < minHz || band.lowHz > maxHz) continue
      const edge0 = xOf(band.lowHz)
      const edge1 = xOf(band.highHz)
      const x0 = clampX(edge0)
      const x1 = clampX(edge1)
      bandSpans.push({ kind: 'band', x0, x1, edge0, edge1, label: null })
      if (edge0 >= 0 && edge0 <= widthPx) bandEdges.push(edge0)
      if (edge1 >= 0 && edge1 <= widthPx) bandEdges.push(edge1)
    }
    bandSpans.sort((a, b) => a.x0 - b.x0)
    for (const span of bandSpans) {
      const band = scheme.bands.find((b) => xOf(b.lowHz) === span.edge0)
      if (band) span.label = place(band.label, span.x0, span.x1, 'center')
    }

    const markers: StripMarker[] = []
    for (const marker of scheme.markers) {
      if (marker.hz < minHz || marker.hz > maxHz) continue
      markers.push({ x: xOf(marker.hz), label: null })
    }
    markers.sort((a, b) => a.x - b.x)
    for (const m of markers) {
      const marker = scheme.markers.find((k) => xOf(k.hz) === m.x)
      if (marker) m.label = place(marker.label, m.x + 5, widthPx, 'left')
    }

    const channelSpans: StripSpan[] = []
    for (const channel of scheme.channels) {
      const low = channel.centerHz - channel.widthHz / 2
      const high = channel.centerHz + channel.widthHz / 2
      if (high < minHz || low > maxHz) continue
      const edge0 = xOf(low)
      const edge1 = xOf(high)
      channelSpans.push({ kind: 'channel', x0: clampX(edge0), x1: clampX(edge1), edge0, edge1, label: null })
      if (edge0 >= 0 && edge0 <= widthPx) channelEdges.push(edge0)
      if (edge1 >= 0 && edge1 <= widthPx) channelEdges.push(edge1)
    }
    channelSpans.sort((a, b) => a.x0 - b.x0 || a.x1 - b.x1)
    for (const span of channelSpans) {
      const channel = scheme.channels.find(
        (c) => xOf(c.centerHz - c.widthHz / 2) === span.edge0 && xOf(c.centerHz + c.widthHz / 2) === span.edge1,
      )
      if (channel) span.label = place(channel.label, span.x0, span.x1, 'center')
    }

    spans.push(...bandSpans, ...channelSpans)

    return {
      scheme,
      color,
      spans,
      markers,
      channelEdges: dedupe(channelEdges, MIN_EDGE_GAP_PX),
      bandEdges: dedupe(bandEdges, 0.5),
    }
  })
}

/**
 * Sorted, with near-coincident values merged. If any two survivors are
 * closer than `minGap` the whole set is dropped: at that density the edges
 * would no longer read as edges.
 */
function dedupe(xs: number[], minGap: number): number[] {
  if (xs.length === 0) return xs
  const sorted = [...xs].sort((a, b) => a - b)
  const out: number[] = [sorted[0]!]
  for (let i = 1; i < sorted.length; i++) {
    const x = sorted[i]!
    if (x - out[out.length - 1]! > 0.5) out.push(x)
  }
  for (let i = 1; i < out.length; i++) {
    if (out[i]! - out[i - 1]! < minGap) return []
  }
  return out
}

export function stripsHeight(count: number): number {
  return count * STRIP_HEIGHT
}

/** Draws strips stacked from y=0; the context's CSS size is widthPx × stripsHeight(n). */
export function drawStrips(ctx: CanvasRenderingContext2D, layouts: readonly StripLayout[], widthPx: number, scale: number): void {
  ctx.save()
  ctx.scale(scale, scale)
  ctx.font = STRIP_FONT
  ctx.textBaseline = 'middle'
  ctx.textAlign = 'center'

  layouts.forEach((layout, i) => {
    const top = i * STRIP_HEIGHT
    const mid = top + STRIP_HEIGHT / 2
    ctx.fillStyle = '#121218'
    ctx.fillRect(0, top, widthPx, STRIP_HEIGHT)

    for (const span of layout.spans) {
      const w = Math.max(1, span.x1 - span.x0)
      ctx.globalAlpha = span.kind === 'band' ? 0.32 : 0.16
      ctx.fillStyle = layout.color
      ctx.fillRect(span.x0, top + 1, w, STRIP_HEIGHT - 2)
      ctx.globalAlpha = span.kind === 'band' ? 0.9 : 0.6
      // Edges only where they are really the span's edges, not the strip's.
      if (span.edge0 >= 0 && span.edge0 <= widthPx) ctx.fillRect(Math.round(span.edge0), top + 1, 1, STRIP_HEIGHT - 2)
      if (span.edge1 >= 0 && span.edge1 <= widthPx) ctx.fillRect(Math.round(span.edge1) - 1, top + 1, 1, STRIP_HEIGHT - 2)
      if (span.label) {
        ctx.globalAlpha = 1
        ctx.fillStyle = '#e8e8f0'
        ctx.fillText(span.label, (span.x0 + span.x1) / 2, mid + 0.5)
      }
    }

    ctx.globalAlpha = 1
    for (const marker of layout.markers) {
      ctx.fillStyle = layout.color
      ctx.beginPath()
      ctx.moveTo(marker.x - 4, top + 1)
      ctx.lineTo(marker.x + 4, top + 1)
      ctx.lineTo(marker.x, top + 7)
      ctx.closePath()
      ctx.fill()
      ctx.fillRect(Math.round(marker.x), top + 1, 1, STRIP_HEIGHT - 2)
      if (marker.label) {
        ctx.textAlign = 'left'
        ctx.fillStyle = '#e8e8f0'
        ctx.fillText(marker.label, marker.x + 5, mid + 0.5)
        ctx.textAlign = 'center'
      }
    }

    ctx.fillStyle = '#2c2c36'
    ctx.fillRect(0, top + STRIP_HEIGHT - 1, widthPx, 1)
  })
  ctx.restore()
}

/** The placed signal, resolved to a footprint on the heat. */
export interface SignalBlock {
  centerHz: number
  widthHz: number
  startMs: number
  airtimeS: number
}

/** A block thinner than this would vanish; an airtime under 3 s is a fraction of a 10 s row. */
const MIN_BLOCK_PX = 3
/** Yellow, for the pinned signal. */
const SIGNAL_COLOR = '#ffd400'

/** "#rrggbb" at an alpha, for the translucent parts drawn in a block's color. */
function withAlpha(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

/**
 * Draws the signal block onto a heat area of widthPx × heightPx CSS pixels at
 * `scale`. A ghost is the same footprint dotted and unfilled: the block that
 * would land if the pointer tapped here.
 */
export function drawSignalBlock(
  ctx: CanvasRenderingContext2D,
  clip: Clip,
  view: View,
  block: SignalBlock,
  widthPx: number,
  heightPx: number,
  scale: number,
  ghost = false,
  color = SIGNAL_COLOR,
): void {
  const x0 = colToX(view, hzToCol(clip, block.centerHz - block.widthHz / 2), widthPx)
  const x1 = colToX(view, hzToCol(clip, block.centerHz + block.widthHz / 2), widthPx)
  const y0 = rowToY(view, timeToRow(clip, block.startMs), heightPx)
  let y1 = rowToY(view, timeToRow(clip, block.startMs + block.airtimeS * 1000), heightPx)
  if (y1 - y0 < MIN_BLOCK_PX) y1 = y0 + MIN_BLOCK_PX
  if (x1 < 0 || x0 > widthPx || y1 < 0 || y0 > heightPx) return

  ctx.save()
  ctx.scale(scale, scale)
  const w = Math.max(2, x1 - x0)
  // Edge guides over the full height, above and below the block: upward they
  // read against the frequency scale and the scheme strips, downward against
  // the rest of the capture, so the span can be compared with any moment.
  ctx.fillStyle = withAlpha(color, 0.35)
  const leftX = Math.round(x0)
  const rightX = Math.round(x0 + w) - 1
  const above = Math.max(0, y0)
  const belowY = Math.round(y1)
  const below = Math.max(0, heightPx - belowY)
  ctx.fillRect(leftX, 0, 1, above)
  ctx.fillRect(rightX, 0, 1, above)
  ctx.fillRect(leftX, belowY, 1, below)
  ctx.fillRect(rightX, belowY, 1, below)
  ctx.strokeStyle = color
  ctx.lineWidth = 1
  if (ghost) {
    ctx.setLineDash([3, 3])
    ctx.globalAlpha = 0.9
  } else {
    ctx.fillStyle = withAlpha(color, 0.28)
    ctx.fillRect(x0, y0, w, y1 - y0)
  }
  ctx.strokeRect(Math.round(x0) + 0.5, Math.round(y0) + 0.5, Math.round(w) - 1, Math.max(1, Math.round(y1 - y0) - 1))
  ctx.setLineDash([])
  // Center tick above and below, so a narrow block still shows where it is.
  ctx.fillStyle = color
  const cx = Math.round((x0 + x1) / 2)
  ctx.fillRect(cx, Math.max(0, y0 - 6), 1, 6)
  ctx.fillRect(cx, y1, 1, 6)
  ctx.restore()
}

// --------------------------------------------------------------- filter

/**
 * Pale cyan-white, for the filter. A neutral on purpose: the filter is a lens
 * over the capture rather than something in the band, so it should not read
 * like the signal block's yellow or the search's pink, both of which mark
 * frequencies someone might use.
 */
const FILTER_COLOR = '#9fe8ff'
const FILTER_FONT = '10px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'

/**
 * Where the filter's passband sits: its -3 dB edges and its center.
 *
 * No veil over the stopband. The heat already carries the filter, since the
 * mask is applied to the readings before they are colored, and darkening a
 * picture that is already showing what the receiver would have seen would undo
 * the one thing the mask exists to show. These lines say where the edges are;
 * the plot itself says what they did.
 */
export function drawFilterEdges(
  ctx: CanvasRenderingContext2D,
  clip: Clip,
  view: View,
  filter: ResolvedFilter,
  widthPx: number,
  heightPx: number,
  scale: number,
): void {
  const x0 = colToX(view, hzToCol(clip, filter.lowHz), widthPx)
  const x1 = colToX(view, hzToCol(clip, filter.highHz), widthPx)
  const cx = colToX(view, hzToCol(clip, filter.centerHz), widthPx)
  if (x1 < 0 || x0 > widthPx) return

  ctx.save()
  ctx.scale(scale, scale)
  ctx.fillStyle = FILTER_COLOR
  ctx.globalAlpha = 0.55
  if (x0 >= 0 && x0 <= widthPx) ctx.fillRect(Math.round(x0), 0, 1, heightPx)
  if (x1 >= 0 && x1 <= widthPx) ctx.fillRect(Math.round(x1) - 1, 0, 1, heightPx)
  if (cx >= 0 && cx <= widthPx) {
    ctx.globalAlpha = 0.3
    ctx.strokeStyle = FILTER_COLOR
    ctx.lineWidth = 1
    ctx.setLineDash([2, 5])
    ctx.beginPath()
    ctx.moveTo(Math.round(cx) + 0.5, 0)
    ctx.lineTo(Math.round(cx) + 0.5, heightPx)
    ctx.stroke()
    ctx.setLineDash([])
  }

  // The label goes inside the passband when it fits, so it names the region it
  // belongs to rather than floating over the part the filter removed.
  const label = `${formatHz(filter.centerHz)} · ${formatHz(filter.bwHz)} wide`
  ctx.font = FILTER_FONT
  ctx.textBaseline = 'top'
  ctx.textAlign = 'center'
  const width = ctx.measureText(label).width
  const mid = (Math.max(0, x0) + Math.min(widthPx, x1)) / 2
  if (width + 8 <= Math.min(widthPx, x1) - Math.max(0, x0) && mid - width / 2 > 0 && mid + width / 2 < widthPx) {
    ctx.globalAlpha = 1
    ctx.fillStyle = 'rgba(8, 8, 12, 0.7)'
    ctx.fillRect(mid - width / 2 - 4, 2, width + 8, 14)
    ctx.fillStyle = FILTER_COLOR
    ctx.fillText(label, mid, 4)
  }
  ctx.restore()
}

// --------------------------------------------------------- signal check

/** The chart across the foot of the plot: a score at every bin of the crop. */
export interface CheckOverlay {
  /** Score per bin from `bin0`; NaN where nothing could be evaluated. */
  scores: Float32Array
  bin0: number
  widthHz: number
  airtimeS: number
  /**
   * Centers the filter's loss budget allows, or null when no filter is in
   * play. Outside it the chart is drawn faint: the score is still the truth
   * about the interference there, but it is not somewhere a transmission can go.
   */
  budget: { lowHz: number; highHz: number } | null
  /** The worst score anywhere, for the caption. */
  peak: number
}

/** A center under consideration: the one under the pointer, or one that was pinned. */
export interface CheckFocus {
  centerHz: number
  /** Where the block is drawn in time; the score does not depend on it. */
  startMs: number
  /** Busy fraction at this center, NaN where the chart has none. */
  score: number
  /** What the filter would cost a transmission here; 0 without one. */
  lossDb: number
}

/**
 * Pink, for the check.
 *
 * It sits outside the ramps the heat uses: `default` and `extended` run blue
 * to yellow, `charolastra` blue through green to red. `twente` is the
 * exception, being magenta across its middle, and there the column is faint
 * by design rather than by accident. Pink also stays clear of the pinned
 * signal's yellow, so the center being inspected and the ones put down never
 * read as the same thing.
 */
const CHECK_COLOR = '#ff4d9e'
const CHECK_RGB = '255, 77, 158'
const SIGNAL_RGB = '255, 212, 0'
const CHART_FONT = '10px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'
const PIN_FONT = 'bold 10px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'

export function formatPercent(fraction: number): string {
  return `${(fraction * 100).toFixed(fraction < 0.1 ? 1 : 0)}%`
}

/**
 * Height of the chart, or 0 when the plot is too short to give it room
 * without burying the data it is meant to explain.
 */
export function checkChartHeight(heightPx: number): number {
  if (heightPx < 80) return 0
  return Math.min(64, Math.round(heightPx * 0.25))
}

/**
 * The column a signal at this center occupies, full height, with the block
 * itself at its start time.
 *
 * The block says where one transmission would sit; the column says what the
 * score actually judged, which is that span across all of time. It is kept
 * weak enough to tint rather than hide: what it frames is the evidence for
 * the number.
 *
 * A pin carries its number from the list, at the top of its block, so a row
 * in the panel and a block on the plot identify each other without reading
 * frequencies off both.
 */
export function drawCheckColumn(
  ctx: CanvasRenderingContext2D,
  clip: Clip,
  view: View,
  focus: CheckFocus,
  overlay: { widthHz: number; airtimeS: number },
  widthPx: number,
  heightPx: number,
  scale: number,
  pinned: boolean,
  label: string | null = null,
): void {
  const x0 = colToX(view, hzToCol(clip, focus.centerHz - overlay.widthHz / 2), widthPx)
  const x1 = colToX(view, hzToCol(clip, focus.centerHz + overlay.widthHz / 2), widthPx)
  if (x1 >= 0 && x0 <= widthPx) {
    ctx.save()
    ctx.scale(scale, scale)
    ctx.fillStyle = `rgba(${pinned ? SIGNAL_RGB : CHECK_RGB}, ${pinned ? 0.08 : 0.12})`
    const left = Math.max(0, x0)
    const right = Math.min(widthPx, x1)
    ctx.fillRect(left, 0, Math.max(1, right - left), heightPx)
    ctx.restore()
  }
  const color = pinned ? SIGNAL_COLOR : CHECK_COLOR
  drawSignalBlock(
    ctx,
    clip,
    view,
    { centerHz: focus.centerHz, widthHz: overlay.widthHz, startMs: focus.startMs, airtimeS: overlay.airtimeS },
    widthPx,
    heightPx,
    scale,
    !pinned,
    color,
  )
  if (label === null) return
  const cx = (x0 + x1) / 2
  if (cx < 0 || cx > widthPx) return
  const y = Math.max(10, rowToY(view, timeToRow(clip, focus.startMs), heightPx) - 2)
  ctx.save()
  ctx.scale(scale, scale)
  ctx.font = PIN_FONT
  ctx.textAlign = 'center'
  ctx.textBaseline = 'bottom'
  // Shadowed, because the label sits over whatever the capture put there.
  ctx.fillStyle = '#000'
  ctx.fillText(label, Math.round(cx) + 1, y + 1)
  ctx.fillStyle = color
  ctx.fillText(label, Math.round(cx), y)
  ctx.restore()
}

/**
 * The busy chart across the bottom of the plot: for every column, the
 * band of scores the bins under it take, with the lowest drawn as a bright
 * contour. Valleys are where the signal fits.
 *
 * Per-column min and max rather than a mean: at a few bins per pixel a mean
 * would hide both the quiet bin worth finding and the busy one worth avoiding.
 *
 * The pointer's span is lit up on the chart and named, so the number under
 * the cursor and the shape it sits in are read together; pinned centers are
 * marked in their own yellow so they stay findable when the pointer has
 * moved on.
 */
export function drawCheckChart(
  ctx: CanvasRenderingContext2D,
  clip: Clip,
  view: View,
  overlay: CheckOverlay,
  focus: CheckFocus | null,
  pins: readonly CheckFocus[],
  widthPx: number,
  heightPx: number,
  scale: number,
): void {
  const chartHeight = checkChartHeight(heightPx)
  if (chartHeight === 0) return
  const top = heightPx - chartHeight
  const plotTop = top + 12
  const plotBottom = heightPx - 5
  const span = Math.max(1, plotBottom - plotTop)
  const yFor = (score: number) => plotBottom - score * span
  const last = overlay.bin0 + overlay.scores.length - 1
  const xOf = (hz: number) => colToX(view, hzToCol(clip, hz), widthPx)

  ctx.save()
  ctx.scale(scale, scale)
  ctx.fillStyle = 'rgba(8, 8, 12, 0.74)'
  ctx.fillRect(0, top, widthPx, chartHeight)
  ctx.fillStyle = 'rgba(255, 255, 255, 0.16)'
  ctx.fillRect(0, top, widthPx, 1)
  ctx.fillRect(0, Math.round(plotBottom), widthPx, 1)

  // The pointer's span, lit from behind the curve so the curve stays legible over it.
  const lit = focus ? [xOf(focus.centerHz - overlay.widthHz / 2), xOf(focus.centerHz + overlay.widthHz / 2)] : null
  if (lit && lit[1]! >= 0 && lit[0]! <= widthPx) {
    const left = Math.max(0, lit[0]!)
    const right = Math.min(widthPx, lit[1]!)
    ctx.fillStyle = `rgba(${CHECK_RGB}, 0.18)`
    ctx.fillRect(left, plotTop, Math.max(1, right - left), plotBottom - plotTop)
  }

  for (let x = 0; x < widthPx; x++) {
    const cA = xToCol(view, x, widthPx)
    const cB = xToCol(view, x + 1, widthPx)
    const from = Math.max(overlay.bin0, Math.round(Math.min(cA, cB)))
    const to = Math.min(last, Math.round(Math.max(cA, cB)))
    let min = Infinity
    let max = -Infinity
    for (let bin = from; bin <= to; bin++) {
      const score = overlay.scores[bin - overlay.bin0]!
      if (Number.isNaN(score)) continue
      if (score < min) min = score
      if (score > max) max = score
    }
    if (min === Infinity) continue
    const yMin = yFor(min)
    const yMax = yFor(max)
    const hz = colToHz(clip, (cA + cB) / 2)
    const affordable = !overlay.budget || (hz >= overlay.budget.lowHz && hz <= overlay.budget.highHz)
    const inside = lit !== null && x + 1 >= lit[0]! && x <= lit[1]!
    ctx.fillStyle = `rgba(${CHECK_RGB}, ${inside ? 0.6 : affordable ? 0.28 : 0.1})`
    ctx.fillRect(x, yMax, 1, Math.max(1, yMin - yMax))
    ctx.fillStyle = inside ? '#fff' : `rgba(${CHECK_RGB}, ${affordable ? 0.95 : 0.3})`
    ctx.fillRect(x, yMin - 1, 1, 2)
  }

  const dot = (at: CheckFocus, color: string) => {
    if (Number.isNaN(at.score)) return
    const x = xOf(at.centerHz)
    if (x < 0 || x > widthPx) return
    ctx.fillStyle = color
    ctx.beginPath()
    ctx.arc(x, yFor(at.score), 2.5, 0, Math.PI * 2)
    ctx.fill()
  }
  for (const pin of pins) dot(pin, SIGNAL_COLOR)
  if (focus) dot(focus, '#fff')

  ctx.font = CHART_FONT
  ctx.textBaseline = 'top'
  ctx.textAlign = 'right'
  ctx.fillStyle = 'rgba(226, 226, 240, 0.55)'
  ctx.fillText('100%', widthPx - 5, top + 2)
  ctx.textBaseline = 'bottom'
  ctx.fillText('0%', widthPx - 5, plotBottom - 1)

  ctx.textBaseline = 'top'
  if (focus) {
    // The number under the pointer, over its own span, clear of the axis labels.
    const text = describeFocus(focus, clip.run.geometry.stepHz)
    const width = ctx.measureText(text).width
    const x = Math.min(widthPx - 34 - width / 2, Math.max(4 + width / 2, xOf(focus.centerHz)))
    ctx.textAlign = 'center'
    ctx.fillStyle = 'rgba(8, 8, 12, 0.85)'
    ctx.fillRect(x - width / 2 - 4, top + 1, width + 8, 12)
    ctx.fillStyle = '#fff'
    ctx.fillText(text, x, top + 2)
  } else {
    // The axis stays 0-100% so two captures compare, but a band that is quiet
    // everywhere would otherwise read as an empty chart rather than good news.
    ctx.textAlign = 'left'
    ctx.fillStyle = 'rgba(226, 226, 240, 0.85)'
    const caption = `% busy · worst ${Math.round(overlay.peak * 100)}%`
    ctx.fillText(overlay.budget ? `${caption} · faint is past the filter` : caption, 5, top + 2)
  }
  ctx.restore()
}

/** "912.150 MHz · busy 12% · filter −1.2 dB", as the chart and the tooltip say it. */
export function describeFocus(focus: CheckFocus, stepHz: number): string {
  const busy = Number.isNaN(focus.score) ? 'no score' : `busy ${formatPercent(focus.score)}`
  const loss = focus.lossDb > 0.05 ? ` · filter −${focus.lossDb.toFixed(1)} dB` : ''
  return `${formatMHz(focus.centerHz, stepHz)} · ${busy}${loss}`
}

/** Continues band edges, channel edges and markers down the heat canvas. */
export function drawEdges(
  ctx: CanvasRenderingContext2D,
  layouts: readonly StripLayout[],
  heightPx: number,
  scale: number,
): void {
  ctx.save()
  ctx.scale(scale, scale)
  for (const layout of layouts) {
    ctx.fillStyle = layout.color
    ctx.globalAlpha = 0.22
    for (const x of layout.channelEdges) ctx.fillRect(Math.round(x), 0, 1, heightPx)
    ctx.globalAlpha = 0.45
    for (const x of layout.bandEdges) ctx.fillRect(Math.round(x), 0, 1, heightPx)
    ctx.globalAlpha = 0.6
    ctx.strokeStyle = layout.color
    ctx.setLineDash([4, 4])
    ctx.lineWidth = 1
    for (const marker of layout.markers) {
      ctx.beginPath()
      ctx.moveTo(Math.round(marker.x) + 0.5, 0)
      ctx.lineTo(Math.round(marker.x) + 0.5, heightPx)
      ctx.stroke()
    }
    ctx.setLineDash([])
  }
  ctx.restore()
}
